import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, useMediaQuery, useTheme, Tabs, Tab, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import Footer from '../components/common/Footer';

// 페이지 컴포넌트 임포트
import DashboardPage from '../pages/dashboard/DashboardPage';
import ActivityDataInputPage from '../pages/data/ActivityDataInputPage';
import PredictionPage from '../pages/predictions/PredictionPage';
import ReportPage from '../pages/reports/ReportPage';

// 페이지 컴포넌트 매핑
const pageComponents = {
  '/dashboard': DashboardPage,
  '/data/input': ActivityDataInputPage,
  '/predictions': PredictionPage,
  '/reports': ReportPage,
  // 필요에 따라 더 많은 경로 추가
};

// 페이지 제목 매핑
const pageTitles = {
  '/dashboard': '대시보드',
  '/data/input': '데이터 입력',
  '/data/history': '데이터 조회',
  '/predictions': '예측 분석',
  '/reports': '보고서',
  '/settings': '설정',
  '/help': '도움말',
  // 필요에 따라 더 많은 경로 추가
};

// 사이드바 너비 상수
// const DRAWER_WIDTH = 240;
// const COLLAPSED_DRAWER_WIDTH = 72;

const DRAWER_WIDTH = 1;
const COLLAPSED_DRAWER_WIDTH = 1;

const MainLayout = ({ onLogout }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // 탭 관련 상태
  const [tabs, setTabs] = useState([{ path: '/dashboard', title: '대시보드', fixed: true }]);
  const [activeTab, setActiveTab] = useState('/dashboard');

  // 현재 경로가 변경될 때 탭 상태 업데이트
  useEffect(() => {
    const currentPath = location.pathname;
    
    // 현재 경로가 이미 탭에 있는지 확인
    const tabExists = tabs.some(tab => tab.path === currentPath);
    
    if (!tabExists && pageTitles[currentPath]) {
      // 새 탭 추가 - 함수형 업데이트 사용
      setTabs(prevTabs => [...prevTabs, { 
        path: currentPath, 
        title: pageTitles[currentPath] || '새 페이지',
        fixed: false
      }]);
    }
    
    // 현재 경로를 활성 탭으로 설정
    setActiveTab(currentPath);
  }, [location.pathname, tabs]);

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleToggleCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleTabChange = (event, newPath) => {
    setActiveTab(newPath);
    navigate(newPath);
  };

  const handleTabClose = (e, path) => {
    e.stopPropagation();
    
    // 고정 탭은 닫을 수 없음
    const tabToClose = tabs.find(tab => tab.path === path);
    if (tabToClose?.fixed) return;
    
    // 탭 목록에서 제거 - 함수형 업데이트 사용
    setTabs(prevTabs => {
      const newTabs = prevTabs.filter(tab => tab.path !== path);
      
      // 닫은 탭이 현재 활성 탭이었다면 다른 탭으로 이동
      if (activeTab === path) {
        const newActiveTab = newTabs[newTabs.length - 1]?.path || '/dashboard';
        setActiveTab(newActiveTab);
        navigate(newActiveTab);
      }
      
      return newTabs;
    });
  };

  // 탭 열기 핸들러 (사이드바에서 호출)
  const handleTabOpen = (path) => {
    // 이미 탭이 열려있는지 확인
    const tabExists = tabs.some(tab => tab.path === path);
    
    // 탭이 없으면 새로 추가 - 함수형 업데이트 사용
    if (!tabExists && pageTitles[path]) {
      setTabs(prevTabs => [...prevTabs, { 
        path: path, 
        title: pageTitles[path] || '새 페이지',
        fixed: false
      }]);
    }
    
    // 해당 탭을 활성화
    setActiveTab(path);
  };

  // 현재 활성 탭에 해당하는 컴포넌트 렌더링
  const renderActiveComponent = () => {
    const Component = pageComponents[activeTab];
    return Component ? <Component /> : <div>개발 진행중입니다. 조금만 기다려주세요.</div>;
  };

  // 사이드바 너비 계산
  const sidebarWidth = sidebarOpen ? (sidebarCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH) : 0;

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #051336 0%, #0A2463 100%)',
      backgroundAttachment: 'fixed'
    }}>
      <Navbar 
        onToggleSidebar={handleToggleSidebar} 
        onLogout={onLogout}
        sidebarOpen={sidebarOpen}
        sidebarCollapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleCollapse}
      />
      
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        <Sidebar 
          open={sidebarOpen} 
          onClose={() => setSidebarOpen(false)} 
          collapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleCollapse}
          onTabOpen={handleTabOpen}
        />
        
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: 'transparent',
            transition: theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            // 사이드바 상태에 따라 동적으로 margin 조정
            ...(isMobile ? {
              marginLeft: 0,
              width: '100%',
            } : {
              marginLeft: `${sidebarWidth}px`,
              width: `calc(100% - ${sidebarWidth}px)`,
            }),
          }}
        >
          {/* 탭 헤더 영역 */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'rgba(76, 185, 231, 0.3)',
              backgroundColor: 'rgba(5, 19, 54, 0.8)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(76, 185, 231, 0.5)',
                borderRadius: '3px',
              },
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: '20px',
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                  height: '3px',
                  boxShadow: '0 0 8px rgba(0, 229, 255, 0.8)',
                },
                '& .MuiTab-root': {
                  minHeight: '20px',
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  fontWeight: 400,
                  transition: 'all 0.3s ease',
                  opacity: 0.7,
                  '&.Mui-selected': {
                    color: 'primary.main',
                    fontWeight: 500,
                    opacity: 1,
                    textShadow: '0 0 5px rgba(0, 229, 255, 0.5)',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 229, 255, 0.05)',
                    color: 'primary.light',
                  },
                },
              }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.path}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {tab.title}
                      {!tab.fixed && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleTabClose(e, tab.path)}
                          sx={{
                            width: 16,
                            height: 16,
                            fontSize: '0.75rem',
                            color: 'inherit',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              color: 'error.light',
                            },
                          }}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      )}
                    </Box>
                  }
                  value={tab.path}
                  sx={{
                    borderRight: tab.fixed ? '1px solid rgba(76, 185, 231, 0.2)' : 'none',
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* 탭 컨텐츠 영역 */}
          <Box
            sx={{ 
              p: { xs: 2, sm: 3 },
              overflow: 'auto',
              flex: 1,
              minHeight: 'calc(100vh - 180px)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 50% 50%, rgba(0, 229, 255, 0.05) 0%, rgba(5, 19, 54, 0) 70%)',
                pointerEvents: 'none',
                zIndex: -1,
              }
            }}
          >
            {renderActiveComponent()}
          </Box>
          <Footer />
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
